import { useTranslation } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';
import { BsIcon } from '@hotelplan/core.basis.bs-icon';
import { useToggleState } from '@hotelplan/libs.hooks-react';
import { track } from '@hotelplan/libs.tracking-events';
import {
  IBookmarkGroupState,
  TBookmarkItem,
} from 'components/domain/fdr-bookmark/fdr-bookmark-types';
import { getBookmarkImageFromFdrBookmark } from 'components/domain/fdr-bookmark/fdr-bookmark.utils';
import { EShareType } from 'components/domain/fdr-share-overlay/fdr-share-overlay.types';
import FdrBookmarkCatalogOverlay from 'components/domain/fdr-share-pdf/fdr-bookmark-pdf-overlay';
import { SiteToolsButtonsWrapper } from 'components/domain/sitetools/SiteTools.common';
import { Tooltip, TooltipWrap } from './fdr-site-tool.styles';

const Wrapper = styled.div({
  marginLeft: '16px',
});

type TBookmarkPdfShareProps = {
  items?: Array<TBookmarkItem>;
  title: string;
  activeGroup: IBookmarkGroupState;
};

enum EEvents {
  OPEN = 'open catalogue popup',
  CLOSE = 'close catalogue popup',
  SHARE = 'share',
}

function getPriceTypeInfo(type: string) {
  return type === 'WITH_PRICES' ? 'mit ab Preis' : 'ohne ab Preise';
}

function getShareTypeInfo(type: string) {
  switch (type) {
    case 'Link kopieren':
      return type;
    case 'mail':
      return `Teilen per Mail`;
    case 'whatsapp':
      return `Teilen per Whatsapp`;
    case 'Download':
      return type;
  }
}

const trackCatalogue = (action: string, language: string) => {
  track({
    event: `bookmark`,
    action,
    label: action === EEvents.CLOSE ? 'Schliessen' : `Katalog anzeigen`,
    category: undefined,
    component: 'catalogue-popup',
    position: undefined,
    language,
    _clear: true,
  });
};

const trackWishlistShare = (
  label: string,
  category: string,
  language: string
) => {
  track({
    event: `bookmark`,
    action: EEvents.SHARE,
    label: getShareTypeInfo(label),
    category: getPriceTypeInfo(category),
    component: 'catalogue-popup',
    position: undefined,
    language,
    _clear: true,
  });
};

const trackOpen = (language: string) => trackCatalogue(EEvents.OPEN, language);
const trackClose = (language: string) =>
  trackCatalogue(EEvents.CLOSE, language);
const trackShare = (label: string, category: string, language: string) =>
  trackWishlistShare(label, category, language);

const FdrBookmarkCatalog: React.FC<TBookmarkPdfShareProps> = ({
  items,
  title,
  activeGroup,
}: TBookmarkPdfShareProps) => {
  const [t, { language }] = useTranslation('common');

  const image = items
    ?.map(i => getBookmarkImageFromFdrBookmark(i))
    .find(i => i);

  const [isOpened, openModal, closeModal] = useToggleState(false);

  const handlePdfClick = () => {
    trackOpen(language);
    openModal();
  };

  return (
    <Wrapper>
      <TooltipWrap className="personalized-catalogue">
        <SiteToolsButtonsWrapper
          onClick={handlePdfClick}
          className="bookmark-site-tools-button-wrapper"
        >
          <BsIcon className="pdf-icon" name="catalog-pdf" size="md" />
          <span className="button-text">
            {t('bookmark.pfd.button.tooltip')}
          </span>
        </SiteToolsButtonsWrapper>
        <Tooltip position={'bottom-left'}>
          {t('bookmark.pfd.button.tooltip')}
        </Tooltip>
      </TooltipWrap>
      {isOpened && (
        <FdrBookmarkCatalogOverlay
          overlayTitle={t('bookmark.bookmark.overlay.pdf.title')}
          pageTitle={title}
          image={image}
          onClose={() => {
            trackClose(language);
            closeModal();
          }}
          activeGroup={activeGroup}
          whiteList={[
            EShareType.MAIL,
            EShareType.WHATSAPP,
            EShareType.DOWNLOAD,
          ]}
          trackFn={trackShare}
        />
      )}
    </Wrapper>
  );
};

export default FdrBookmarkCatalog;
